import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "../Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useDisableBackgroundScrolling } from "../../../hooks/DisableBackgroundScrolling";

export interface ModalProps {
  id?: string;
  imgSrc?: string;
  showImg?: boolean;
  imgBlurColor?: string;
  showClose?: boolean;
  onClose?: () => void;
  children?: ReactNode;
  className?: string;
  modalWidth?: string;
  autoClose?: boolean;
  closeButtonClassName?: string;
  isOpen: boolean;
}

export function Modal({
  id,
  imgSrc,
  showImg = true,
  imgBlurColor = "bg-[#FFE4D4]",
  showClose = true,
  onClose,
  children,
  className,
  modalWidth = "w-full max-w-[400px]",
  autoClose = false,
  closeButtonClassName,
  isOpen = false,
}: ModalProps) {


  useDisableBackgroundScrolling(isOpen, className);
  useEffect(() => {
    if (!autoClose || !onClose) return;

    const timeout = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timeout);
  }, [autoClose, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      id={id}
      className={`!fixed inset-0 z-[997] bg-black/70 ${className ?? ""}`}
    >
      <div
        className={`!fixed top-1/2 left-1/2 px-4 -translate-x-1/2 -translate-y-1/2 ${modalWidth}`}
      >
        <div className="relative rounded-2xl bg-white p-6 text-center shadow-xl">
          {showClose && onClose && (
            <Button
              onClick={onClose}
              className={`absolute top-4 right-4 h-6 w-6 rounded-full bg-[#F2F2F2] text-[#838383] ${closeButtonClassName ?? ""}`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          )}

          {showImg && (
            <div className="relative mx-auto flex h-[125px] w-[125px] items-center justify-center">
              <div
                className={`absolute inset-0 rounded-full blur-[25px] ${imgBlurColor}`}
              />

              <div className="relative z-10 flex h-[64px] w-[64px] items-center justify-center rounded-full bg-white">
                <img
                  src={imgSrc || "/icons/ic-info.svg"}
                  decoding="sync"
                  loading="eager"
                  fetchPriority="high"
                  alt="Info"
                  className="h-[40px] w-[40px]"
                />
              </div>
            </div>
          )}

          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}